/**
 * Line-based modification specifications helpers.
 * Storage remains JSON (prefer string[]); no schema change.
 */

export type SpecInput = string | string[] | Record<string, unknown> | null | undefined;

/** Strip common Word/Docs bullets and numbered prefixes. */
export function stripSpecificationBullet(line: string): string {
  return line
    .replace(/^[\s]*[-*•●◦▪▫]\s+/, '')
    .replace(/^[\s]*\d+[.)]\s+/, '')
    .trim();
}

/** Parse textarea / pasted text into specification lines. */
export function parseSpecificationLines(value: string): string[] {
  if (!value) return [];
  return value
    .split(/\r?\n|;/)
    .map((s) => stripSpecificationBullet(s))
    .filter(Boolean);
}

/** Normalize for storage as newline-separated text (textarea ↔ lines). */
export function normalizeSpecificationText(value: string): string {
  return parseSpecificationLines(value).join('\n');
}

/** Lines ready for bullet display; accepts legacy objects and JSON strings. */
export function formatSpecificationDisplay(value: SpecInput): string[] {
  if (value == null || value === '') return [];

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return formatSpecificationDisplay(JSON.parse(trimmed) as SpecInput);
      } catch {
        // fall through as plain text
      }
    }
    return parseSpecificationLines(trimmed);
  }

  if (Array.isArray(value)) {
    return value
      .map(String)
      .map((s) => stripSpecificationBullet(s))
      .filter(Boolean);
  }

  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => {
        if (/^spec_\d+$/i.test(k)) {
          return String(v ?? '');
        }
        return `${k}: ${v}`;
      })
      .map((s) => stripSpecificationBullet(s))
      .filter(Boolean);
  }

  return [];
}

/** Textarea value when loading existing specs. */
export function specificationsToTextarea(value: SpecInput): string {
  return formatSpecificationDisplay(value).join('\n');
}

/** Persist as JSON string array of lines. */
export function specificationsForStorage(value: string): string[] {
  return parseSpecificationLines(value);
}
